import { ChangeEvent, useEffect, useState } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutationResult } from "@tanstack/react-query";
import Button from "@material-ui/core/Button";
import AddCircle from "@material-ui/icons/AddCircle";
import Dialog from "@material-ui/core/Dialog";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import Chip from "@material-ui/core/Chip";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import WatchLater from "@material-ui/icons/WatchLater";
import InputMask from "react-input-mask";
import { schema } from "./schema";
import { useExpedientFormStyles } from "./styles";
import { useAttendanceLocalStyles } from "../../../../../styles";
import { AttendanceLocal } from "../../../../../../../@types/attendance-local";
import { AttendanceLocalExpedientUpdateData } from "../../../../../../../services/api-tansquery/attendance-local/expedient/update";
import { AttendanceLocalExpedientCreateData } from "../../../../../../../services/api-tansquery/attendance-local/expedient/create";

type ExpedientFormProps = {
  slug: AttendanceLocal["slug_unidade"];
  expedient?: AttendanceLocal["horario_unidade"];
  mutation: UseMutationResult<
    AttendanceLocalExpedientCreateData | AttendanceLocalExpedientUpdateData,
    unknown,
    AttendanceLocalExpedientCreateData | AttendanceLocalExpedientUpdateData,
    unknown
  >
};

const weekDays = [
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
  "Domingo",
];

export default function ExpedientForm({ slug, expedient, mutation }: ExpedientFormProps) {
  const [open, setOpen] = useState(false);
  const { classes } = useExpedientFormStyles();
  const { classes: defaultClasses } = useAttendanceLocalStyles();
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      unidade: slug,
      dia_semana: [],
      intervalo_ativo: false,
      hora_inicio_manha: null,
      hora_fim_manha: null,
      hora_inicio_tarde: null,
      hora_fim_tarde: null,
    },
  });

  useEffect(() => {
    if (open && expedient) {
      form.reset({
        unidade: slug,
        dia_semana: expedient.dia_semana || [],
        intervalo_ativo: expedient.intervalo_ativo || false,
        hora_inicio_manha: expedient.hora_inicio_manha?.slice(0, 5) || null,
        hora_fim_manha: expedient.hora_fim_manha?.slice(0, 5) || null,
        hora_inicio_tarde: expedient.hora_inicio_tarde?.slice(0, 5) || null,
        hora_fim_tarde: expedient.hora_fim_tarde?.slice(0, 5) || null,
      });
    } else {
      form.reset();
    }
  }, [open, expedient, form, slug]);

  async function handleSubmit(data: z.infer<typeof schema>) {
    if (mutation.isLoading) {
      return;
    }

    if (!data.intervalo_ativo) {
      data.hora_fim_manha = null;
      data.hora_inicio_tarde = null;
    }

    await mutation.mutateAsync({
      unidade: data.unidade,
      dia_semana: data.dia_semana,
      intervalo_ativo: data.intervalo_ativo,
      hora_inicio_manha: data.hora_inicio_manha,
      hora_fim_manha: data.hora_fim_manha,
      hora_inicio_tarde: data.hora_inicio_tarde,
      hora_fim_tarde: data.hora_fim_tarde,
    });
  }

  useEffect(() => {
    if (mutation.isSuccess) {
      setOpen(false);
    }
  }, [mutation.isSuccess]);

  return (
    <>
      <Button
        startIcon={<AddCircle fontSize="small" />}
        className={defaultClasses.addButton}
        onClick={() => setOpen(true)}
      >
        Horários
      </Button>
      <Dialog open={open}>
        <Card
          component="form"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <CardHeader
            avatar={<AddCircle />}
            className={classes.cardHeader}
            title={
              <Typography variant="h5">Horário de Funcionamento</Typography>
            }
          />
          <CardContent
            container
            spacing={2}
            component={Grid}
          >
            <Grid item xs={12}>
              <Controller
                name="dia_semana"
                control={form.control}
                render={({ field, fieldState }) => (
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    error={!!fieldState.error}
                  >
                    <InputLabel id="week-days-label">Dias da semana</InputLabel>
                    <Select
                      multiple
                      id="week-days"
                      labelId="week-days-label"
                      value={field.value || []}
                      label="Dias da semana"
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                      }}
                      renderValue={(selected) => (
                        <div className={classes.chips}>
                          {(selected as string[]).map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              className={classes.chip}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              onDelete={() => {
                                field.onChange(
                                  field.value.filter((v: string) => v !== value)
                                );
                              }}
                            />
                          ))}
                        </div>
                      )}
                    >
                      {weekDays.map((weekDay) => (
                        <MenuItem key={weekDay} value={weekDay}>
                          {weekDay}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {fieldState.error && fieldState.error.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="hora_inicio_manha"
                control={form.control}
                render={({ field, fieldState }) => (
                  <InputMask
                    mask="99:99"
                    maskChar={null}
                    value={field.value || ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e.target.value);
                    }}
                  >
                    {(inputProps: any) => (
                      <TextField
                        {...inputProps}
                        required
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        label="Início do funcionamento"
                        inputProps={{ className: classes.textField }}
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error ? fieldState.error.message : ""
                        }
                        InputProps={{
                          required: false,
                          endAdornment: <WatchLater htmlColor="#C4C4C4" />
                        }}
                      />
                    )}
                  </InputMask>
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="hora_fim_tarde"
                control={form.control}
                render={({ field, fieldState }) => (
                  <InputMask
                    mask="99:99"
                    maskChar={null}
                    value={field.value || ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e.target.value);
                    }}
                  >
                    {(inputProps: any) => (
                      <TextField
                        {...inputProps}
                        required
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        label="Fim do funcionamento"
                        inputProps={{ className: classes.textField }}
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error ? fieldState.error.message : ""
                        }
                        InputProps={{
                          required: false,
                          endAdornment: <WatchLater htmlColor="#C4C4C4" />
                        }}
                      />
                    )}
                  </InputMask>
                )}
              />
            </Grid>

            <Grid item xs={12} style={{ paddingLeft: 16 }}>
              <Controller
                name="intervalo_ativo"
                control={form.control}
                render={({ field }) => (
                  <FormControl>
                    <FormControlLabel
                      label="Órgão tem intervalo no funcionamento?"
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e, checked) => {
                            field.onChange(checked);
                          }}
                        />
                      }
                    />
                    <FormHelperText>
                      Marque se o intervalo de almoço estiver ativo.
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {form.watch("intervalo_ativo") && (
              <>
                <Grid item xs={6}>
                  <Controller
                    name="hora_fim_manha"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <InputMask
                        mask="99:99"
                        maskChar={null}
                        value={field.value || ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.value);
                        }}
                      >
                        {(inputProps: any) => (
                          <TextField
                            {...inputProps}
                            required
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            label="Início do intervalo"
                            inputProps={{ className: classes.textField }}
                            error={!!fieldState.error}
                            helperText={
                              fieldState.error ? fieldState.error.message : ""
                            }
                            InputProps={{
                              required: false,
                              endAdornment: <WatchLater htmlColor="#C4C4C4" />
                            }}
                          />
                        )}
                      </InputMask>
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Controller
                    name="hora_inicio_tarde"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <InputMask
                        mask="99:99"
                        maskChar={null}
                        value={field.value || ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.value);
                        }}
                      >
                        {(inputProps: any) => (
                          <TextField
                            {...inputProps}
                            required
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            label="Fim do intervalo"
                            inputProps={{ className: classes.textField }}
                            error={!!fieldState.error}
                            helperText={
                              fieldState.error ? fieldState.error.message : ""
                            }
                            InputProps={{
                              required: false,
                              endAdornment: <WatchLater htmlColor="#C4C4C4" />
                            }}
                          />
                        )}
                      </InputMask>
                    )}
                  />
                </Grid>
              </>
            )}
          </CardContent>
          <CardActions className={classes.cardActions}>
            <Button
              className={classes.button}
              onClick={() => setOpen(false)}
              disabled={mutation.isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className={defaultClasses.addButton}
              disabled={mutation.isLoading}
            >
              {mutation.isLoading ? "Salvando..." : "Salvar"}
            </Button>
          </CardActions>
        </Card>
      </Dialog>
    </>
  );
}
